import React from 'react';
import axios from "axios";

import { Container, Grid, useMantineTheme, Box, Group, Flex } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconFilter, IconReload, IconCirclePlus } from '@tabler/icons-react';

import { useDisplayModeContext } from "../../context/displayModeContext";
import { useLoginContext } from "../../context/loginContext";

import { BE_API_ONE, utils } from "../../utils/globalVariables";
import { regexNoStriped, isoDateToSqlDate } from "../../utils/globalFunctions";

import AmountOfData from "../../components/general/AmountOfData";
import DisplayModeSelector from "../../components/general/DisplayModeSelector";
import ActionIconComponent from "../../components/general/ActionIconComponent";

import NoData from "../../components/general/NoData";
import WrapperUtils from "../../layout/WrapperUtils";

import StockItemCard from "./StockItemCard";
import StockItemTable from "./StockItemTable";
import StockItemAdd from "./StockItemAdd";
import StockItemEdit from "./StockItemEdit";
import StockItemDelete from "./StockItemDelete";
import StockItemView from "./StockItemView";
import StockItemFilter from "./StockItemFilter";
import StockItemTagging from "./StockItemTagging";
import StockItemMoveStokRusak from "./StockItemMoveStokRusak";
import StockItemKlaimGaransi from "./StockItemKlaimGaransi";
import SearchInputComponent from "../../components/general/SearchInputComponent";

const StockItemIndex = () => {

    const mountedRef = React.useRef(true);

    const theme = useMantineTheme();
    const matchesMinMD = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
    const { displayMode } = useDisplayModeContext();
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorGetData: 'stockItemIndex-1',
    })

    const filterDefaultRef = React.useRef({
        stockCategory: {
            show: false,
            id: utils.flagAll,
            name: '',
            isActive: '',
        },
        stockCategorySub: {
            show: false,
            id: utils.flagAll,
            name: '',
            isActive: '',
            categoryId: '',
            categoryName: '',
        },
        stockSupplier: {
            show: false,
            id: utils.flagAll,
            name: '',
            address: '',
            contact: '',
            isActive: '',
        },
        stockIsActive: utils.flagAll,
        stockTanggalBeliDari: null,
        stockTanggalBeliSampai: null,
        stockTanda: utils.flagAll,
        stockGaransiType: utils.flagAll,
    })

    const sortList = {
        stockCategory: {
            slug: 'stock_category_name',
            caption: 'Kategori',
            type: utils.sortType.string.slug
        },
        stockCategorySub: {
            slug: 'stock_category_sub_name',
            caption: 'Sub Kategori',
            type: utils.sortType.string.slug
        },
        stockSupplier: {
            slug: 'supplier_name',
            caption: 'Supplier',
            type: utils.sortType.string.slug
        },
        stockTanggalBeli: {
            slug: 'stock_tanggal_beli',
            caption: 'Tanggal Pembelian',
            type: utils.sortType.date.slug
        },
        stockHargaBeli: {
            slug: 'stock_harga_beli',
            caption: 'Harga Pembelian',
            type: utils.sortType.integer.slug
        },
        stockHargaJual: {
            slug: 'stock_harga_jual',
            caption: 'Harga Penjualan',
            type: utils.sortType.integer.slug
        },
        stockMasaGaransiType: {
            slug: 'stock_masa_garansi_type',
            caption: 'Garansi',
            type: utils.sortType.string.slug
        },
        stockMasaGaransi: {
            slug: 'stock_masa_garansi_number',
            caption: 'Masa Garansi',
            type: utils.sortType.integer.slug
        },
        stockJumlah: {
            slug: 'stock_jumlah',
            caption: 'Jumlah Stok',
            type: utils.sortType.integer.slug
        },
        stockTanda: {
            slug: 'stock_status',
            caption: 'Tanda',
            type: utils.sortType.string.slug
        },
        stockIsActive: {
            slug: 'stock_is_active',
            caption: 'Status',
            type: utils.sortType.integer.slug
        },
    }

    const [data, setData] = React.useState({
        data: [],
        dataCount: 0,
        dataFlag: 'idle',
        dataLoading: false,
        dataRefresh: new Date(),
        dataOffset: 0,
        dataSortSelected: sortList.stockCategory,
        dataSearchInputForBE: '',

        filterData: {
            drawer: false,
            stockCategory: filterDefaultRef.current.stockCategory,
            stockCategorySub: filterDefaultRef.current.stockCategorySub,
            stockSupplier: filterDefaultRef.current.stockSupplier,
            stockIsActive: filterDefaultRef.current.stockIsActive,
            stockTanggalBeliDari: filterDefaultRef.current.stockTanggalBeliDari,
            stockTanggalBeliSampai: filterDefaultRef.current.stockTanggalBeliSampai,
            stockTanda: filterDefaultRef.current.stockTanda,
            stockGaransiType: filterDefaultRef.current.stockGaransiType
        },

        addData: {
            drawer: false,
        },

        editData: {
            drawer: false,
            data: []
        },

        tagging: {
            drawer: false,
            preventNotUpdate: new Date(),
            data: []
        },

        deleteData: {
            drawer: false,
            data: []
        },

        moveStokRusak: {
            drawer: false,
            data: []
        },

        klaimGaransi: {
            drawer: false,
            data: [],
            trigger: new Date()
        },

        viewData: {
            drawer: false,
            data: []
        },
    });

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, []);

    const fnSort = (stateSortSelected) => {
        setData(d => ({
            ...d,
            dataLoading: true,
            dataSortSelected: {
                ...d.dataSortSelected,
                slug: d.dataSortSelected.slug[0] === '-' ? stateSortSelected.slug : '-' + stateSortSelected.slug,
                caption: stateSortSelected.caption,
                type: stateSortSelected.type
            }
        }));
    };

    const AmountOfDataComponent = () => {
        return (
            <AmountOfData
                stateDataLimit={utils.dataLimit}
                stateDataOffset={data.dataOffset}
                stateAmountDataAll={data.dataCount}
                stateDataFlag={data.dataFlag}
                stateSuffix=''

                fnFirstData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnLastData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnPreviousData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnNextData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}
            />
        )
    }

    React.useEffect(() => {
        setData(d => ({ ...d, dataLoading: true }));

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);
        formDT.append('data_limit', utils.dataLimit);
        formDT.append('data_offset', data.dataOffset);
        formDT.append('order_by', regexNoStriped(data.dataSortSelected.slug));
        formDT.append('order_type', data.dataSortSelected.slug[0] === '-' ? 'asc' : 'desc');

        formDT.append('flag_all', utils.flagAll);
        formDT.append('filter_search', data.dataSearchInputForBE);
        formDT.append('filter_stock_category', data.filterData.stockCategory.id);
        formDT.append('filter_stock_category_sub', data.filterData.stockCategorySub.id);
        formDT.append('filter_stock_supplier', data.filterData.stockSupplier.id);
        formDT.append('filter_stock_is_active', data.filterData.stockIsActive);

        formDT.append('filter_stock_tanggal_beli_dari', data.filterData.stockTanggalBeliDari === null ? utils.flagAll : isoDateToSqlDate(data.filterData.stockTanggalBeliDari, ''));
        formDT.append('filter_stock_tanggal_beli_sampai', data.filterData.stockTanggalBeliSampai === null ? utils.flagAll : isoDateToSqlDate(data.filterData.stockTanggalBeliSampai, ''));
        formDT.append('filter_stock_tanda', data.filterData.stockTanda);
        formDT.append('filter_stock_garansi_type', data.filterData.stockGaransiType);

        //

        formDT.append('tagged_only', '0');
        formDT.append('project_id', utils.flagAll);
        formDT.append('project_process', utils.flagAll);
        formDT.append('project_is_cancel', utils.flagAll);
        formDT.append('stock_item_tagging_status_obj', JSON.stringify(utils.stockItemStatus));
        formDT.append('stock_item_garansi_status_obj', JSON.stringify(utils.statusItemGaransi));

        axios({
            method: 'post',
            url: BE_API_ONE + '/get_stock',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, dataLoading: false }));

            if (response.data.flag_status === 'isset') {
                setData(d => ({
                    ...d,
                    data: response.data.data,
                    dataCount: response.data.data_count,
                    dataFlag: response.data.data.length > 0 ? true : false,
                }));
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({
                ...d,
                dataLoading: false,
            }));

            notifications.show({
                id: notificationId.current.errorGetData,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }, [handleSetLoginContext, loginContext.syswebapp, data.dataRefresh, data.dataSearchInputForBE, data.dataSortSelected.slug, data.dataOffset, data.filterData.stockCategory.id, data.filterData.stockCategorySub.id, data.filterData.stockSupplier.id, data.filterData.stockIsActive, data.filterData.stockTanggalBeliDari, data.filterData.stockTanggalBeliSampai, data.filterData.stockTanda, data.filterData.stockGaransiType])

    return (
        <React.Fragment>
            <WrapperUtils
                stateDataLoading={data.dataLoading}
            >
                <Group spacing='xs' noWrap>
                    <ActionIconComponent
                        stateVariant='light'
                        stateRadius='xl'
                        stateTitle='Filter'
                        stateSize='lg'
                        fnOnClick={() => setData(d => ({
                            ...d,
                            filterData: {
                                ...d.filterData,
                                drawer: true
                            }
                        }))}

                        StateIcon={IconFilter}
                        stateIconSize='1.125rem'
                        stateIconStrokeWidth={1.25}
                        stateIconStyle={{
                            color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                        }}
                    />

                    <ActionIconComponent
                        stateVariant='light'
                        stateRadius='xl'
                        stateTitle='Add'
                        stateSize='lg'
                        fnOnClick={() => setData(d => ({
                            ...d,
                            addData: {
                                ...d.addData,
                                drawer: true
                            }
                        }))}

                        StateIcon={IconCirclePlus}
                        stateIconSize='1.25rem'
                        stateIconStrokeWidth={1.25}
                        stateIconStyle={{
                            color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                        }}
                    />

                    <DisplayModeSelector />

                    <ActionIconComponent
                        stateVariant='light'
                        stateRadius='xl'
                        stateTitle='Reload Data'
                        stateSize='lg'
                        fnOnClick={() => setData(d => ({ ...d, dataLoading: true, dataRefresh: new Date() }))}

                        StateIcon={IconReload}
                        stateIconSize='1.125rem'
                        stateIconStrokeWidth={1.25}
                        stateIconStyle={{
                            color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                        }}
                    />

                    <SearchInputComponent
                        statePlaceHolder='Search Stok'
                        fnDoSearch={(val) => setData(d => ({
                            ...d,
                            dataOffset: 0,
                            dataSearchInputForBE: val,
                            dataRefresh: new Date(),
                        }))}
                    />
                </Group>

                {matchesMinMD &&
                    <Flex
                        sx={{ flex: 1 }}
                        justify="flex-end"
                        align="center"
                        direction="row"
                        wrap="nowrap"
                    >
                        <AmountOfDataComponent />
                    </Flex>
                }
            </WrapperUtils>

            <Container fluid>
                <Grid my={0}>
                    {data.data.length <= 0 && data.dataFlag !== 'idle' &&
                        <Grid.Col span={12}>
                            <NoData />
                        </Grid.Col>
                    }

                    {data.data.length > 0 && displayMode === utils.displayMode.card.slug &&
                        <Grid.Col
                            span={12}
                            py={0}
                        >
                            <Box mt='md'>
                                <StockItemCard
                                    stateData={data.data}
                                    stateSortList={sortList}
                                    stateSortSelected={data.dataSortSelected}
                                    fnSort={(val) => {
                                        fnSort(val);
                                    }}

                                    fnSelectedDataView={(val) => setData(d => ({
                                        ...d,
                                        viewData: {
                                            ...d.viewData,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedDataEdit={(val) => setData(d => ({
                                        ...d,
                                        editData: {
                                            ...d.editData,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedDataTagging={(val) => setData(d => ({
                                        ...d,
                                        tagging: {
                                            ...d.tagging,
                                            data: val,
                                            drawer: true,
                                            preventNotUpdate: new Date()
                                        }
                                    }))}

                                    fnSelectedDataDelete={(val) => setData(d => ({
                                        ...d,
                                        deleteData: {
                                            ...d.deleteData,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedInStock={(val) => setData(d => ({
                                        ...d,
                                        moveStokRusak: {
                                            ...d.moveStokRusak,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedKlaimGaransi={(val) => setData(d => ({
                                        ...d,
                                        klaimGaransi: {
                                            ...d.klaimGaransi,
                                            data: val,
                                            trigger: new Date(),
                                            drawer: true
                                        }
                                    }))}
                                />
                            </Box>
                        </Grid.Col>
                    }

                    {data.data.length > 0 && displayMode === utils.displayMode.table.slug &&
                        <Grid.Col
                            span={12}
                            py={0}
                        >
                            <Box mt='md'>
                                <StockItemTable
                                    stateDataOffset={data.dataOffset}
                                    stateData={data.data}
                                    stateSortList={sortList}
                                    stateSortSelected={data.dataSortSelected}
                                    fnSort={(val) => {
                                        fnSort(val);
                                    }}

                                    fnSelectedDataView={(val) => setData(d => ({
                                        ...d,
                                        viewData: {
                                            ...d.viewData,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedDataEdit={(val) => setData(d => ({
                                        ...d,
                                        editData: {
                                            ...d.editData,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedDataTagging={(val) => setData(d => ({
                                        ...d,
                                        tagging: {
                                            ...d.tagging,
                                            data: val,
                                            drawer: true,
                                            preventNotUpdate: new Date()
                                        }
                                    }))}

                                    fnSelectedDataDelete={(val) => setData(d => ({
                                        ...d,
                                        deleteData: {
                                            ...d.deleteData,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedInStock={(val) => setData(d => ({
                                        ...d,
                                        moveStokRusak: {
                                            ...d.moveStokRusak,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedKlaimGaransi={(val) => setData(d => ({
                                        ...d,
                                        klaimGaransi: {
                                            ...d.klaimGaransi,
                                            data: val,
                                            trigger: new Date(),
                                            drawer: true
                                        }
                                    }))}
                                />
                            </Box>
                        </Grid.Col>
                    }

                    {data.data.length > 0 &&
                        <Grid.Col span={12} className='amountOfDataBottom'>
                            <Group mt='xs' mb='md' position='apart'>
                                <Box>&nbsp;</Box>

                                <AmountOfDataComponent />
                            </Group>
                        </Grid.Col>
                    }
                </Grid>
            </Container>

            <StockItemFilter
                stateShow={data.filterData.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    filterData: {
                        ...d.filterData,
                        drawer: false
                    }
                }))}

                stateDefaultFilter={{
                    ...filterDefaultRef.current,
                }}

                stateFilter={{
                    stockCategory: data.filterData.stockCategory,
                    stockCategorySub: data.filterData.stockCategorySub,
                    stockSupplier: data.filterData.stockSupplier,
                    stockIsActive: data.filterData.stockIsActive,
                    stockTanggalBeliDari: data.filterData.stockTanggalBeliDari,
                    stockTanggalBeliSampai: data.filterData.stockTanggalBeliSampai,
                    stockTanda: data.filterData.stockTanda,
                    stockGaransiType: data.filterData.stockGaransiType,
                }}

                fnFilter={(category, categorySub, supplier, isActive, tglBeliDari, tglBeliSampai, tanda, garansiType) => {

                    setData(d => ({
                        ...d,
                        filterData: {
                            ...d.filterData,
                            stockCategory: {
                                ...d.filterData.stockCategory,
                                id: category
                            },
                            stockCategorySub: {
                                ...d.filterData.stockCategorySub,
                                id: categorySub
                            },
                            stockSupplier: {
                                ...d.filterData.stockSupplier,
                                id: supplier
                            },
                            stockIsActive: isActive,
                            stockTanggalBeliDari: tglBeliDari,
                            stockTanggalBeliSampai: tglBeliSampai,
                            stockTanda: tanda,
                            stockGaransiType: garansiType,

                            drawer: false
                        },

                        dataRefresh: new Date(),
                        dataLoading: true,
                    }));
                }}
            />

            <StockItemAdd
                stateShow={data.addData.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    addData: {
                        ...d.addData,
                        drawer: false
                    }
                }))}

                fnSuccessAdd={() => {
                    setData(d => ({
                        ...d,
                        addData: {
                            ...d.addData,
                            drawer: false
                        },
                        dataRefresh: new Date(),
                        dataLoading: true,
                    }))
                }}
            />

            <StockItemTagging
                stateShow={data.tagging.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    tagging: {
                        ...d.tagging,
                        drawer: false
                    }
                }))}

                stateData={data.tagging.data}
                statePreventNotUpdate={data.tagging.preventNotUpdate}

                fnSuccessTagging={() => {
                    setData(d => ({
                        ...d,
                        tagging: {
                            ...d.tagging,
                            drawer: false
                        },
                        dataRefresh: new Date(),
                        dataLoading: true,
                    }))
                }}
            />

            <StockItemEdit
                stateShow={data.editData.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    editData: {
                        ...d.editData,
                        drawer: false
                    }
                }))}

                stateData={data.editData.data}

                fnSuccessEdit={() => {
                    setData(d => ({
                        ...d,
                        editData: {
                            ...d.editData,
                            drawer: false
                        },
                        dataRefresh: new Date(),
                        dataLoading: true,
                    }))
                }}
            />

            <StockItemDelete
                stateShow={data.deleteData.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    deleteData: {
                        ...d.deleteData,
                        drawer: false
                    }
                }))}

                stateData={data.deleteData.data}
                fnSuccessDelete={() => {
                    setData(d => ({
                        ...d,
                        deleteData: {
                            ...d.deleteData,
                            drawer: false
                        },
                        dataRefresh: new Date(),
                        dataLoading: true,
                    }))
                }}
            />

            <StockItemMoveStokRusak
                stateShow={data.moveStokRusak.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    moveStokRusak: {
                        ...d.moveStokRusak,
                        drawer: false
                    }
                }))}

                stateData={data.moveStokRusak.data}
                fnSuccess={() => {
                    setData(d => ({
                        ...d,
                        moveStokRusak: {
                            ...d.moveStokRusak,
                            drawer: false
                        },
                        dataRefresh: new Date(),
                        dataLoading: true,
                    }))
                }}
            />

            <StockItemKlaimGaransi
                stateShow={data.klaimGaransi.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    klaimGaransi: {
                        ...d.klaimGaransi,
                        drawer: false
                    }
                }))}

                stateTrigger={data.klaimGaransi.trigger}

                stateData={data.klaimGaransi.data}
                fnSuccessKlaim={() => {
                    setData(d => ({
                        ...d,
                        klaimGaransi: {
                            ...d.klaimGaransi,
                            drawer: false
                        },
                        dataRefresh: new Date(),
                        dataLoading: true,
                    }))
                }}
            />

            <StockItemView
                stateShow={data.viewData.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    viewData: {
                        ...d.viewData,
                        drawer: false
                    }
                }))}

                stateData={data.viewData.data}
            />
        </React.Fragment>
    )
}

export default StockItemIndex;